/**
 * Sprint 4.D — Types de l'analyse IA d'un DCE.
 * Mirroir des DTOs backend `apps/api/src/modules/tm/application/dto/dce-analysis.dto.ts`.
 */

export type DceAnalysisStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'DONE'
  | 'PARTIAL_SUCCESS'
  | 'FAILED';

export type DceAnalysisTrigger = 'AUTO' | 'MANUAL';

export type DceItemHumanStatus =
  | 'EXTRACTED'
  | 'VALIDATED'
  | 'REJECTED'
  | 'CORRECTED';

export type DceRequirementType =
  | 'technique'
  | 'fonctionnelle'
  | 'administrative'
  | 'qualite'
  | 'securite'
  | 'rgpd'
  | 'methodologique';

export type DceProfileSeniority =
  | 'junior'
  | 'confirme'
  | 'senior'
  | 'expert'
  | 'lead';

export type DceRequiredPieceType =
  | 'KBIS'
  | 'ATTESTATION_URSSAF'
  | 'ATTESTATION_FISCALE'
  | 'ASSURANCE_RC_PRO'
  | 'ISO_9001'
  | 'ISO_14001'
  | 'ISO_27001'
  | 'DC1'
  | 'DC2'
  | 'DC4'
  | 'DUME'
  | 'REFERENCES'
  | 'CV_EQUIPE'
  | 'RIB'
  | 'POUVOIR'
  | 'AUTRE';

export type SubmissionFormat =
  | 'papier'
  | 'electronique'
  | 'plateforme_dematerialisee';

export interface DceAnalysisCounts {
  requirements: number;
  requirementsValidated: number;
  profiles: number;
  profilesValidated: number;
  requiredDocuments: number;
  requiredDocumentsValidated: number;
}

export interface AwardCriterionJson {
  name: string;
  weightPercent: number;
  type: 'prix' | 'technique' | 'delai' | 'methode' | 'autre';
  subCriteria: string[];
  source: {
    documentId: number;
    pageNumber: number | null;
    excerpt: string;
  };
  confidence: number;
  reasoning: string | null;
}

export interface OpportunityConstraints {
  executionLocation: string | null;
  requiredLanguage: string | null;
  submissionFormat: SubmissionFormat | null;
  submissionPlatform: string | null;
  mandatorySiteVisit: boolean | null;
  siteVisitDate: string | null;
  legalConditions: Record<string, unknown>;
  securityConstraints: Record<string, unknown>;
  awardCriteria: AwardCriterionJson[] | unknown;
}

export interface DceAnalysis {
  id: number;
  dceUploadId: number;
  opportunityId: number;
  status: DceAnalysisStatus;
  isCurrent: boolean;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  modelUsed: string | null;
  totalTokens: number;
  totalCostUsd: number;
  partialFailures: Array<{ source: string; reason: string }>;
  requirementsApplicable: boolean;
  profilesApplicable: boolean;
  criteriaApplicable: boolean;
  constraintsApplicable: boolean;
  profilesAppliedReason: string | null;
  triggeredBy: DceAnalysisTrigger;
  triggeredByUserId: number | null;
  previousAnalysisId: number | null;
  migratedItemsCount: number;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  counts?: DceAnalysisCounts;
  opportunityConstraints?: OpportunityConstraints;
}

export interface DceRequirement {
  id: number;
  analysisId: number;
  lotId: number | null;
  statement: string;
  type: DceRequirementType;
  mandatory: boolean;
  originalRef: string | null;
  sourceDocumentId: number;
  sourceDocumentFilename?: string;
  sourcePageNumber: number | null;
  sourceExcerpt: string;
  confidence: number;
  reasoning: string | null;
  humanStatus: DceItemHumanStatus;
  humanNotes: string | null;
  migratedFromAnalysisId: number | null;
  lastUpdatedById: number | null;
  lastUpdatedByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DceProfile {
  id: number;
  analysisId: number;
  lotId: number | null;
  jobTitle: string;
  description: string | null;
  technicalSkills: string[];
  functionalSkills: string[];
  seniority: DceProfileSeniority | null;
  yearsExperienceMin: number | null;
  certifications: string[];
  languages: string[];
  workUnits: string | null;
  sourceDocumentId: number;
  sourceDocumentFilename?: string;
  sourcePageNumber: number | null;
  sourceExcerpt: string;
  confidence: number;
  reasoning: string | null;
  humanStatus: DceItemHumanStatus;
  humanNotes: string | null;
  lastUpdatedById: number | null;
  lastUpdatedByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DceRequiredDocument {
  id: number;
  analysisId: number;
  pieceType: DceRequiredPieceType;
  mandatory: boolean;
  notes: string | null;
  sourceDocumentId: number;
  sourceDocumentFilename?: string;
  sourcePageNumber: number | null;
  sourceExcerpt: string;
  confidence: number;
  reasoning: string | null;
  humanStatus: DceItemHumanStatus;
  humanNotes: string | null;
  lastUpdatedById: number | null;
  lastUpdatedByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BulkValidateRequest {
  ids: number[];
  status: 'VALIDATED' | 'REJECTED';
}

export interface BulkValidateResponse {
  updated: number[];
  notFound: number[];
  alreadyInStatus: number[];
}

export interface ListRequirementsFilters {
  humanStatus?: DceItemHumanStatus;
  type?: DceRequirementType;
  lotId?: number;
  search?: string;
}

export interface PresignedDownloadUrl {
  url: string;
  expiresAt: string;
  expiresInSeconds: number;
}

export interface RunAnalysisResponse {
  analysisId: number;
  status: DceAnalysisStatus;
  enqueued: boolean;
  reason: 'created' | 'idempotent-existing' | 'stuck-recovered';
}

export function isTerminalAnalysisStatus(s: DceAnalysisStatus): boolean {
  return s === 'DONE' || s === 'PARTIAL_SUCCESS' || s === 'FAILED';
}

/** Couleur de badge pour la confidence (vert ≥ 0.8, orange 0.5-0.8, rouge < 0.5). */
export function confidenceColor(c: number): 'green' | 'amber' | 'red' {
  if (c >= 0.8) return 'green';
  if (c >= 0.5) return 'amber';
  return 'red';
}
